import * as React from "react";
import styled from "@emotion/styled";
import { useForm } from "react-hook-form";
import {
  Button,
  Callout,
  FieldError,
  FlexRow,
  Form,
  Input,
  Label,
  Link,
  Text,
} from "atoms";
import { useAuth } from "features/auth";
import { ApiError } from "utils/api";
import { useUpdateEmail } from "../api/useUpdateEmail";
import { validateEmail } from "../utils/validateEmail";

type ProfileEmailFields = { email: string; verificationCode?: string };

function EditProfileEmail() {
  // Grab user profile data from Auth context, Router ensures they're already signed in
  const { user } = useAuth();

  const [isVerifying, setIsVerifying] = React.useState(false);

  const { mutate, isLoading, isSuccess, isError, error } = useUpdateEmail();

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<ProfileEmailFields>({
    defaultValues: {
      email: user?.email ?? "",
    },
  });

  const onSubmit = (fields: ProfileEmailFields) =>
    mutate(fields, {
      onSuccess: (resp) => {
        if (resp.status === "VERIFICATION_SENT") {
          setIsVerifying(true);
        } else if (resp.status === "EMAIL_UPDATED") {
          setIsVerifying(false);
        }
      },
    });

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Label>
        Email <small>(Not displayed publicly)</small>
        <Input
          {...register("email", {
            validate: (value) =>
              !value ||
              validateEmail(value) ||
              "Please enter a valid email address.",
          })}
          disabled={isVerifying}
        />
        {errors.email && <FieldError>{errors.email.message}</FieldError>}
      </Label>

      {isVerifying ? (
        <Callout>
          <Text size="lg" bold>
            Verify your email address
          </Text>

          <Text>Enter the verification code sent to your email address.</Text>

          <Label>
            Verification Code
            <Input
              {...register("verificationCode", {
                required: "This field is required",
              })}
              maxLength={6}
            />
            {errors.verificationCode && (
              <FieldError>{errors.verificationCode.message}</FieldError>
            )}
          </Label>

          <SFlexRow>
            <Button
              type="submit"
              variant="gradient"
              size="md"
              disabled={isLoading}
            >
              Submit Code
            </Button>
          </SFlexRow>
        </Callout>
      ) : (
        <FlexRow>
          {isSuccess ? (
            <Success>Thanks! Your email has been verified.</Success>
          ) : (
            <>
              <Button
                type="submit"
                variant="gradient"
                size="md"
                disabled={isLoading || !isDirty}
              >
                Save
              </Button>

              <Link to="/profile" variant="primary" size="md">
                Cancel
              </Link>
            </>
          )}
        </FlexRow>
      )}

      {isError && (
        <FieldError>
          {error instanceof ApiError
            ? error.message
            : "Sorry, something went wrong."}
        </FieldError>
      )}
    </Form>
  );
}
const Success = styled(Text)(({ theme }) => ({
  borderRadius: theme.borderRadius.round,
  backgroundColor: theme.colors.green,
  padding: ".2em 1em",
  fontSize: theme.fontSizes.xs,
  fontWeight: "bold",
  marginTop: ".5rem",
  display: "inline-block",
}));

const SFlexRow = styled(FlexRow)({ marginTop: "2rem" });

export { EditProfileEmail };
